import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaSlidersH, FaInfoCircle } from 'react-icons/fa';
import { useTitle } from '../../../context/TitleContext';
import { showErrorToast, showSuccessToast } from '../../../shared/utils/toastHelper';
import {
  fetchAdminAppSettings,
  updateAdminAppSettings,
} from '../../../redux/Slices/AdminAppSettingsSlice';
import { apiGet } from '../../../shared/utils/apiConfig'; // تأكد أن المسار صحيح

const INITIAL_STATE = {
  about_app_ar: '',
  about_app_en: '',
  terms_ar: '',
  terms_en: '',
  face_link: '',
  linked_link: '',
  twitter_link: '',
  what_number: '',
  phone: '',
  kilo_price: '',
  pharmacy_name_ar: '',
  pharmacy_name_en: '',
  pharmacy_img: '',
  pharmacy_img_url: '',
  clinic_name_ar: '',
  clinic_name_en: '',
  clinic_img: '',
  clinic_img_url: '',
};

const resolveSettingsPayload = (payload) => {

  if (!payload || typeof payload !== 'object') {
    return INITIAL_STATE;
  }

  // أولوية الريسبونس: إذا فيه result استخدمه، إذا لا استخدم payload نفسه
  const source = payload.result || payload;


  // helper: sanitize url fields like ".../[]"
  const normalizeUrl = (val) => {
    if (!val) return '';
    const s = String(val);
    if (s === '[]' || s.endsWith('/[]')) return '';
    return s;
  };

  return {
    about_app_ar: source?.about_app_ar ?? '',
    about_app_en: source?.about_app_en ?? '',
    terms_ar: source?.terms_ar ?? '',
    terms_en: source?.terms_en ?? '',
    face_link: source?.face_link ?? '',
    linked_link: source?.linked_link ?? '',
    twitter_link: source?.twitter_link ?? '',
    what_number: source?.what_number !== undefined && source?.what_number !== null ? String(source.what_number) : '',
    phone: source?.phone !== undefined && source?.phone !== null ? String(source.phone) : '',
    kilo_price:
      source?.kilo_price !== undefined && source?.kilo_price !== null
        ? String(source.kilo_price)
        : '',
    pharmacy_name_ar: source?.pharmacy_name_ar ?? '',
    pharmacy_name_en: source?.pharmacy_name_en ?? '',
    pharmacy_img: Array.isArray(source?.pharmacy_img) ? '' : (source?.pharmacy_img ?? ''),
    pharmacy_img_url: normalizeUrl(source?.pharmacy_img_url),
    clinic_name_ar: source?.clinic_name_ar ?? '',
    clinic_name_en: source?.clinic_name_en ?? '',
    clinic_img: Array.isArray(source?.clinic_img) ? '' : (source?.clinic_img ?? ''),
    clinic_img_url: normalizeUrl(source?.clinic_img_url),
  };
};

const AdminAppSettings = () => {
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const { data, loading, error, saving, saveError } = useSelector(
    (state) => state.adminAppSettings
  );
  const [formData, setFormData] = useState(INITIAL_STATE);

  // Debug logs
  const currentData = useMemo(() => {
    const resolved = resolveSettingsPayload(data);
    return resolved;
  }, [data]);



  useEffect(() => {
    setTitle('إعدادات التطبيق');
  }, [setTitle]);

  useEffect(() => {
    dispatch(fetchAdminAppSettings());
  }, [dispatch]);


  useEffect(() => {
    setFormData(currentData);
  }, [currentData]);

  useEffect(() => {
    if (error) {
      showErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (saveError) {
      showErrorToast(saveError);
    }
  }, [saveError]);

  // إضافة حالة جديدة لسعر الكشف الافتراضي للعيادة من الاندبوينت
  const [defaultClinicPrice, setDefaultClinicPrice] = useState('');


  //   const fetchDefaultClinicPrice = async () => {
  //     try {
  //       const res = await apiGet('/doctor_dash/show_doctor_clinic_price', { clinic_id: 3 });
  //       const price =
  //         res?.result?.price ??
  //         res?.price ??
  //         res?.data?.price ??
  //         res?.data?.result?.price ??
  //         '';
  //       setDefaultClinicPrice(price !== undefined && price !== null ? String(price) : '');
  //     } catch (err) {
  //       setDefaultClinicPrice('');
  //     }
  //   };
  //   fetchDefaultClinicPrice();
  // }, []);

  const handleChange = (event) => {
    const { name, value, files } = event.target;
    if (name === 'pharmacy_img' || name === 'clinic_img') {
      const file = files && files[0] ? files[0] : null;
      setFormData((prev) => ({
        ...prev,
        [name]: file || '',
      }));
      return;
    }
      if (name === 'kilo_price') {
        setFormData((prev) => ({
          ...prev,
          [name]: value.replace(/[^\d]/g, ''),
        }));
        return;
      }
      // أرقام الهاتف والواتساب: لا تصفِّها هنا، فقط خذ القيمة كما هي
      if (name === 'what_number' || name === 'phone') {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
        return;
      }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isDirty = useMemo(() => {
    return Object.keys(formData).some((key) => formData[key] !== currentData[key]);
  }, [formData, currentData]);

  const buildPayload = () => {
    const payload = {};
    // إرسال جميع البيانات مع البيانات المتغيرة
    Object.keys(formData).forEach((key) => {
      if (key === 'kilo_price') {
        payload[key] = formData[key] === '' ? null : Number(formData[key]);
      } else {
        payload[key] = formData[key] === '' ? null : formData[key];
      }
    });

    // إذا كان هناك ملفات صور، استخدم FormData
    const hasFiles =
      payload.pharmacy_img instanceof File ||
      payload.clinic_img instanceof File;

    if (hasFiles) {
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        if (v === null || v === undefined) return;
        fd.append(k, v);
      });
      return fd;
    }

    if ('pharmacy_img' in payload && !(payload.pharmacy_img instanceof File)) {
      delete payload.pharmacy_img;
    }
    if ('clinic_img' in payload && !(payload.clinic_img instanceof File)) {
      delete payload.clinic_img;
    }

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isDirty) {
      showErrorToast('يرجى إدخال بيانات قبل الحفظ');
      return;
    }

    const payload = buildPayload();

    // التحقق من وجود بيانات داخل الـ payload حتى عند كونه FormData
    const hasChanges =
      payload instanceof FormData
        ? (() => {
            let count = 0;
            for (const _ of payload.entries()) {
              count++;
              if (count > 0) break;
            }
            return count > 0;
          })()
        : Object.keys(payload).length > 0;

    if (!hasChanges) {
      showErrorToast('لم يتم إجراء أي تغييرات للحفظ');
      return;
    }

    dispatch(updateAdminAppSettings(payload))
      .unwrap()
      .then(() => {
        showSuccessToast('تم حفظ الإعدادات بنجاح');
        // بعد الحفظ الناجح، جلب الإعدادات من جديد من السيرفر
        dispatch(fetchAdminAppSettings());
      })
      .catch((err) => {
        showErrorToast(err || 'فشل في تحديث إعدادات التطبيق');
      });
  };

  return (
    <div className="main_content">
      <div className="dashboard-container content-wrapper admin-app-settings-page">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0 d-flex align-items-center gap-2" style={{ fontSize: '20px' }}>
            <FaSlidersH style={{ color: '#184194' }} />
            إعدادات التطبيق
          </h2>
        </div>

      

        <div className="card shadow-sm border-0">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">جاري تحميل الإعدادات...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="settings-form">
                <div className="row g-4">
                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">عن التطبيق (عربي)</label>
                    <textarea
                      className="form-control"
                      name="about_app_ar"
                      value={formData.about_app_ar}
                      onChange={handleChange}
                      rows={4}
                      placeholder="اكتب نبذة عن التطبيق باللغة العربية"
                    />
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">About App (English)</label>
                    <textarea
                      className="form-control"
                      name="about_app_en"
                      value={formData.about_app_en}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Write about the app in English"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">الشروط والأحكام (عربي)</label>
                    <textarea
                      className="form-control"
                      name="terms_ar"
                      value={formData.terms_ar}
                      onChange={handleChange}
                      rows={4}
                      placeholder="أدخل الشروط والأحكام باللغة العربية"
                    />
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">Terms & Conditions (English)</label>
                    <textarea
                      className="form-control"
                      name="terms_en"
                      value={formData.terms_en}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Enter the terms and conditions in English"
                    />
                  </div>

                  <div className="col-lg-4">
                    <label className="form-label fw-semibold">رابط فيسبوك</label>
                    <input
                      type="url"
                      className="form-control"
                      name="face_link"
                      value={formData.face_link}
                      onChange={handleChange}
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <div className="col-lg-4">
                    <label className="form-label fw-semibold">رابط لينكدإن</label>
                    <input
                      type="url"
                      className="form-control"
                      name="linked_link"
                      value={formData.linked_link}
                      onChange={handleChange}
                      placeholder="https://linkedin.com/..."
                    />
                  </div>
                  <div className="col-lg-4">
                    <label className="form-label fw-semibold">رابط تويتر</label>
                    <input
                      type="url"
                      className="form-control"
                      name="twitter_link"
                      value={formData.twitter_link}
                      onChange={handleChange}
                      placeholder="https://twitter.com/..."
                    />
                  </div>

                  <div className="col-lg-4">
                    <label className="form-label fw-semibold">رقم الواتساب</label>
                    <input
                      type="tel"
                      className="form-control"
                      name="what_number"
                      value={formData.what_number}
                      onChange={handleChange}
                      placeholder="أدخل رقم الواتساب"
                    />
                  </div>
                  <div className="col-lg-4">
                    <label className="form-label fw-semibold">رقم الهاتف</label>
                    <input
                      type="tel"
                      className="form-control"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="أدخل رقم الهاتف"
                    />
                  </div>

                  <div className="col-lg-4">
                    <label className="form-label fw-semibold">سعر الكيلومتر</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        step="0.01"
                        name="kilo_price"
                        value={formData.kilo_price}
                        onChange={handleChange}
                        placeholder="0.00"
                      />
                      <span className="input-group-text">جنيه</span>
                    </div>
                  </div>
                </div>

                <hr className="my-4" />

                <div className="row g-4">
                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">اسم الصيدلية (عربي)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="pharmacy_name_ar"
                      value={formData.pharmacy_name_ar}
                      onChange={handleChange}
                      placeholder="أدخل اسم الصيدلية بالعربية"
                    />
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">Pharmacy Name (English)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="pharmacy_name_en"
                      value={formData.pharmacy_name_en}
                      onChange={handleChange}
                      placeholder="Enter pharmacy name in English"
                    />
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">صورة الصيدلية</label>
                    <input
                      type="file"
                      accept="image/*"
                      className="form-control"
                      name="pharmacy_img"
                      onChange={handleChange}
                    />
                    {(formData.pharmacy_img_url || (typeof formData.pharmacy_img === 'string' && formData.pharmacy_img)) && (
                      <div className="mt-2 text-center">
                        <img
                          src={formData.pharmacy_img_url || formData.pharmacy_img}
                          alt="صورة الصيدلية"
                          style={{ maxWidth: '140px', maxHeight: '120px', objectFit: 'cover', borderRadius: '10px' }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <hr className="my-4" />

                <div className="row g-4">
                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">اسم العيادة (عربي)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="clinic_name_ar"
                      value={formData.clinic_name_ar}
                      onChange={handleChange}
                      placeholder="أدخل اسم العيادة بالعربية"
                    />
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">Clinic Name (English)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="clinic_name_en"
                      value={formData.clinic_name_en}
                      onChange={handleChange}
                      placeholder="Enter clinic name in English"
                    />
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">صورة العيادة</label>
                    <input
                      type="file"
                      accept="image/*"
                      className="form-control"
                      name="clinic_img"
                      onChange={handleChange}
                    />
                    {(formData.clinic_img_url || (typeof formData.clinic_img === 'string' && formData.clinic_img)) && (
                      <div className="mt-2 text-center">
                        <img
                          src={formData.clinic_img_url || formData.clinic_img}
                          alt="صورة العيادة"
                          style={{ maxWidth: '140px', maxHeight: '120px', objectFit: 'cover', borderRadius: '10px' }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="d-flex justify-content-center mt-4 mb-2">
                  <button
                    type="submit"
                    className="btn d-flex align-items-center gap-2"
                    disabled={saving}
                    style={{ backgroundColor: 'rgb(24, 65, 148)', borderColor: 'rgb(24, 65, 148)', color: '#fff' }}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" />
                        جاري الحفظ...
                      </>
                    ) : (
                      'حفظ الإعدادات'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .admin-app-settings-page .settings-form textarea {
          min-height: 140px;
          resize: vertical;
        }

        .admin-app-settings-page .input-group-text {
          background: #f5f7fb;
          color: #1f2937;
          font-weight: 600;
        }

        .admin-app-settings-page .form-control:focus {
          box-shadow: 0 0 0 0.2rem rgba(24, 65, 148, 0.15);
          border-color: #184194;
        }
      `}</style>
    </div>
  );
};

export default AdminAppSettings;

